import React, { useCallback, useEffect, useState } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import Skeleton from 'react-loading-skeleton'
import { UncontrolledTooltip } from 'reactstrap'
import { useDispatch, useSelector } from 'react-redux'
import userDummyImg from 'assets/images/users/user-dummy-img.jpg'
import { fetchContactsThunk } from 'slices/creators/thunk'
import { AppDispatch, RootState } from 'store'
import { Contact, defaultCreatorContactsFilters } from 'types/creators'
import { SocialAccount } from 'types/influencer'
import { formatNumber, generateUniqueKey } from 'utils/helpers'
import inModal from 'utils/hoc/inModal'
import './SelectCreatorModal.scss'
import { Campaign } from 'types/campaign'
import { Application } from 'types/application'
import { fetchApplicationsThunk } from 'slices/applications/thunk'
import { Link } from 'react-router-dom'
import inOffcanvas from 'utils/hoc/inOffcanvas'
import CreatorFiltersModal from 'Components/creator/CreatorFiltersModal'

interface SelectCreatorModalProps {
    isActive: boolean
    onClose: () => void
    campaign: Campaign
    onSelect: (creator: Contact, socialAccount: SocialAccount) => void
}

const SEARCH_DEBOUNCE_MS = 400
const MIN_SEARCH_LENGTH = 3

const platformIconClass = (platform?: string) => {
    if (platform === 'ig') return 'ri-instagram-line select-creator-modal__platform-icon select-creator-modal__platform-icon--ig'
    if (platform === 'yt') return 'ri-youtube-line select-creator-modal__platform-icon select-creator-modal__platform-icon--yt'
    return 'ri-global-line select-creator-modal__platform-icon'
}

const formatHandle = (handle?: string) => {
    if (!handle) return '—'
    return handle.startsWith('@') ? handle : `@${handle}`
}

export const isSocialAccountPlatformMatch = (
    account: SocialAccount,
    campaignPlatform?: string
) => !!campaignPlatform && account.platform === campaignPlatform

export type DisableReason = 'platform_mismatch' | 'existing_application'

export const hasApplicationForCampaignSocialAccount = (
    account: SocialAccount,
    campaignUuid: string | undefined,
    applications: Application[]
): boolean => {
    if (!campaignUuid) return false
    return applications.some(
        (application) =>
            application.campaign.uuid === campaignUuid &&
            application.social_account.uuid === account.uuid
    )
}

export const getSocialAccountDisableReason = (
    account: SocialAccount,
    campaignPlatform?: string,
    campaignUuid?: string,
    applications: Application[] = []
): DisableReason | null => {
    if (campaignPlatform && !isSocialAccountPlatformMatch(account, campaignPlatform)) {
        return 'platform_mismatch'
    }
    if (hasApplicationForCampaignSocialAccount(account, campaignUuid, applications)) {
        return 'existing_application'
    }
    return null
}

const DISABLE_REASON_MESSAGES: Record<DisableReason, string> = {
    platform_mismatch: 'This creator is not available for the campaign platform',
    existing_application: 'This creator already has an application for this campaign',
}

const getContactKey = (contact: Contact) =>
    contact.social_account?.uuid ?? contact.influencer?.uuid ?? String(contact.id)

export const getCreatorDisableReason = (
    contact: Contact,
    campaignPlatform?: string,
    campaignUuid?: string,
    applications: Application[] = []
): DisableReason | null => {
    const account = contact.social_account
    if (!account) return null
    return getSocialAccountDisableReason(account, campaignPlatform, campaignUuid, applications)
}

export const isCreatorDisabledForCampaign = (
    contact: Contact,
    campaignPlatform?: string,
    campaignUuid?: string,
    applications: Application[] = []
): boolean => getCreatorDisableReason(contact, campaignPlatform, campaignUuid, applications) !== null

interface CreatorStatCellProps {
    iconClass: string
    value: string | number
    label: string
}

const CreatorStatCell: React.FC<CreatorStatCellProps> = ({ iconClass, value, label }) => (
    <div className="select-creator-modal__stat-cell">
        <div className="select-creator-modal__stat-value-row">
            <i className={iconClass} aria-hidden />
            <span className="select-creator-modal__stat-value">{value}</span>
        </div>
        <span className="select-creator-modal__stat-label">{label}</span>
    </div>
)

interface CreatorListItemProps {
    contact: Contact
    isSelected: boolean
    disabled: boolean
    disabledMessage: string
    onSelect: () => void
}

const CreatorListItem: React.FC<CreatorListItemProps> = ({
    contact,
    isSelected,
    disabled,
    disabledMessage,
    onSelect,
}) => {
    const socialAccount = contact.social_account
    const platform = socialAccount?.platform ?? contact.platform
    const avatar = socialAccount?.profile_picture_url.split('=')[0] || userDummyImg
    const name = socialAccount?.name ?? contact.influencer?.name ?? '—'
    const handle = socialAccount?.handle ?? contact.handle
    const followersCount = formatNumber(socialAccount?.followers_count ?? 0)
    const averageViews = formatNumber(socialAccount?.average_views ?? 0)
    const engagementScore = socialAccount?.engagement_score ?? '—'
    const contactKey = getContactKey(contact)
    const rowId = `select-creator-row-${contactKey}`

    return (
        <>
            <button
                key={contactKey}
                id={rowId}
                type="button"
                aria-disabled={disabled}
                aria-label={disabled ? `${name}, ${disabledMessage}` : undefined}
                tabIndex={disabled ? -1 : undefined}
                className={`select-creator-modal__item${isSelected ? ' select-creator-modal__item--selected' : ''}${disabled ? ' select-creator-modal__item--disabled' : ''
                    }`}
                onClick={() => !disabled && onSelect()}
            >
                <input
                    type="radio"
                    name="select-creator"
                    className="select-creator-modal__radio"
                    checked={isSelected}
                    disabled={disabled}
                    onChange={() => !disabled && onSelect()}
                    onClick={(e) => e.stopPropagation()}
                />
                <div className="select-creator-modal__item-main">
                    <div className="select-creator-modal__avatar-wrap">
                        <img src={avatar} alt={name} className="select-creator-modal__avatar" />
                        <span className="select-creator-modal__platform-badge">
                            <i className={platformIconClass(platform)} aria-hidden />
                        </span>
                    </div>
                    <div className="select-creator-modal__item-content">
                        <div className="select-creator-modal__item-name">{name}</div>
                        <div className="select-creator-modal__item-handle">{formatHandle(handle)}</div>
                    </div>
                </div>
                <div className="select-creator-modal__stats-box">
                    <CreatorStatCell iconClass="ri-group-line" value={followersCount} label="Followers" />
                    <CreatorStatCell iconClass="ri-pulse-line" value={engagementScore} label="Engagement" />
                    <CreatorStatCell iconClass="ri-eye-line" value={averageViews} label="Avg Views" />
                </div>
            </button>
            {disabled && (
                <UncontrolledTooltip placement="top" target={rowId}>
                    {disabledMessage}
                </UncontrolledTooltip>
            )}
        </>
    )
}

const SelectCreatorModal: React.FC<SelectCreatorModalProps> = ({
    isActive,
    onClose,
    onSelect,
    campaign,
}) => {
    const [filters, setFilters] = useState(defaultCreatorContactsFilters)
    const campaignPlatform = campaign?.platform
    const campaignUuid = campaign?.uuid
    const dispatch = useDispatch<AppDispatch>()
    const loading = useSelector((state: RootState) => state.creators.loading)
    const applications = useSelector((state: RootState) => state.applications.applications)

    const [filterId, setFilterId] = useState(generateUniqueKey(filters, ['page']))
    const [searchInput, setSearchInput] = useState('')
    const [initialLoading, setInitialLoading] = useState(true)
    const [selectedCreator, setSelectedCreator] = useState<Contact | null>(null)
    const [filtersModalOpen, setFiltersModalOpen] = useState(false)

    const creators = useSelector((state: RootState) => state.creators.filtered_lists[filterId!])
    const creatorsList = creators?.list ?? []
    const has_more = creators?.has_more ?? true
    const page = creators?.page ?? 1

    useEffect(() => {
        setFilters({ ...defaultCreatorContactsFilters, platform: campaignPlatform })
    }, [campaignPlatform])

    const isCreatorDisabled = useCallback(
        (contact: Contact) =>
            isCreatorDisabledForCampaign(contact, campaignPlatform, campaignUuid, applications),
        [campaignPlatform, campaignUuid, applications]
    )

    const getCreatorDisabledMessage = useCallback(
        (contact: Contact) => {
            const reason = getCreatorDisableReason(contact, campaignPlatform, campaignUuid, applications)
            return reason ? DISABLE_REASON_MESSAGES[reason] : ''
        },
        [campaignPlatform, campaignUuid, applications]
    )

    const fetchCreators = useCallback(
        async (nextFilters: typeof filters, force = false) => {
            return await dispatch(fetchContactsThunk({ force, filters: nextFilters }))
        },
        [dispatch]
    )

    useEffect(() => {
        if (!isActive) return
        setInitialLoading(true)
        fetchCreators(filters).finally(() => {
            setFilterId(generateUniqueKey(filters, ['page']))
            setInitialLoading(false)
        })
    }, [filters, isActive, fetchCreators])

    useEffect(() => {
        if (!isActive) return

        const timeout = setTimeout(() => {
            const trimmed = searchInput.trim()
            const q = trimmed.length >= MIN_SEARCH_LENGTH ? trimmed : undefined

            setFilters((prev) => {
                const prevQ = (prev.q ?? '').trim() || undefined
                if (q === prevQ) return prev

                const next = { ...prev, page: 1 }
                if (q) {
                    next.q = q
                } else {
                    delete next.q
                }
                return next
            })
        }, SEARCH_DEBOUNCE_MS)

        return () => clearTimeout(timeout)
    }, [searchInput, isActive])

    useEffect(() => {
        if (!isActive) {
            setSearchInput('')
            setFilters(defaultCreatorContactsFilters)
            setFilterId(generateUniqueKey(defaultCreatorContactsFilters, ['page']))
            setSelectedCreator(null)
        }
    }, [isActive])

    const nextPage = () => {
        if (has_more) {
            dispatch(fetchContactsThunk({ filters: { ...filters, page: page + 1 } }))
        }
    }

    useEffect(() => {
        if (!isActive) return
        dispatch(fetchApplicationsThunk({ force: false }))
    }, [isActive, dispatch])

    const handleSelectCreator = (contact: Contact) => {
        if (isCreatorDisabled(contact)) return
        setSelectedCreator(contact)
    }

    const handleConfirm = () => {
        if (!selectedCreator?.social_account) return
        if (isCreatorDisabled(selectedCreator)) return
        onSelect(selectedCreator, selectedCreator.social_account)
        onClose()
    }

    const showSkeleton = initialLoading || (loading && creatorsList.length === 0)
    const canConfirm =
        !!selectedCreator?.social_account && !isCreatorDisabled(selectedCreator)
    const trimmedSearch = searchInput.trim()
    const showMinLengthHint =
        trimmedSearch.length > 0 && trimmedSearch.length < MIN_SEARCH_LENGTH
    const hasSearchQuery = !!(filters.q?.trim())
    const campaignPlatformLabel =
        campaignPlatform === 'ig'
            ? 'Instagram'
            : campaignPlatform === 'yt'
                ? 'YouTube'
                : null

    return (
        <>
            <div className="select-creator-modal">
                <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
                    <div className="select-creator-modal__search m-0 w-75">
                        <div className="select-creator-modal__search-input-wrap">
                            <i className="ri-search-line select-creator-modal__search-icon" aria-hidden />
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search creators (min. 3 characters)..."
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                aria-describedby={showMinLengthHint ? 'select-creator-search-hint' : undefined}
                            />
                        </div>
                        {showMinLengthHint && (
                            <p id="select-creator-search-hint" className="select-creator-modal__search-hint">
                                Type at least {MIN_SEARCH_LENGTH} characters to search
                            </p>
                        )}
                    </div>
                    <button
                        type="button"
                        className="btn btn-white d-flex justify-content-between align-items-center gap-1"
                        onClick={() => setFiltersModalOpen(true)}
                    >
                        <i className="ri-filter-line" />
                        <span>Filter</span>
                    </button>
                </div>
                <div className="select-creator-modal__body">
                    <div id="select-creator-scroll" className="select-creator-modal__scroll">
                        {showSkeleton &&
                            Array.from({ length: 6 }).map((_, index) => (
                                <div key={index} className="px-3 py-2">
                                    <Skeleton height={72} className="rounded-2" />
                                </div>
                            ))}

                        {!showSkeleton && (
                            <InfiniteScroll
                                dataLength={creatorsList.length}
                                next={nextPage}
                                hasMore={has_more}
                                loader={<></>}
                                scrollableTarget="select-creator-scroll"
                            >
                                {creatorsList.length === 0 && (
                                    <div className="select-creator-modal__empty">
                                        <i
                                            className={`ri-${hasSearchQuery ? 'search' : 'group'}-line select-creator-modal__empty-icon`}
                                            aria-hidden
                                        />
                                        {hasSearchQuery ? (
                                            <>
                                                <p className="select-creator-modal__empty-title">
                                                    No matching creators
                                                </p>
                                                <p className="select-creator-modal__empty-text">
                                                    Try a different name or handle, or clear your search
                                                    to see all creators.
                                                </p>
                                            </>
                                        ) : (
                                            <>
                                                <p className="select-creator-modal__empty-title">
                                                    No creators yet
                                                </p>
                                                <p className="select-creator-modal__empty-text">
                                                    {campaignPlatformLabel
                                                        ? `You don't have any ${campaignPlatformLabel} creators in your account. Add one from the Creators page to continue.`
                                                        : 'Add creators to your account from the Creators page to select them for this campaign.'}
                                                </p>
                                                <Link
                                                    to="/creators"
                                                    className="theme-btn btn-primary select-creator-modal__empty-cta"
                                                    onClick={onClose}
                                                >
                                                    Go to Creators
                                                </Link>
                                            </>
                                        )}
                                    </div>
                                )}
                                {creatorsList.map((contact) => {
                                    const creatorDisabled = isCreatorDisabled(contact)
                                    const creatorDisabledMessage = getCreatorDisabledMessage(contact)
                                    const isSelected =
                                        !creatorDisabled &&
                                        selectedCreator !== null &&
                                        getContactKey(selectedCreator) === getContactKey(contact)

                                    return (
                                        <CreatorListItem
                                            key={getContactKey(contact)}
                                            contact={contact}
                                            isSelected={isSelected}
                                            disabled={creatorDisabled}
                                            disabledMessage={creatorDisabledMessage}
                                            onSelect={() => handleSelectCreator(contact)}
                                        />
                                    )
                                })}
                            </InfiniteScroll>
                        )}
                    </div>
                </div>

                <div className="select-creator-modal__footer">
                    <button type="button" className="btn btn-light" onClick={onClose}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="theme-btn btn-primary"
                        disabled={!canConfirm}
                        onClick={handleConfirm}
                    >
                        Select
                    </button>
                </div>
            </div>
            <CreatorFiltersModal
                isOpen={filtersModalOpen}
                onClose={() => setFiltersModalOpen(false)}
                currentFilters={filters}
                onApply={(next) => setFilters(next)}
            />
        </>
    )
}

export default inOffcanvas(SelectCreatorModal, 'Select Creator')
